import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { buildSchoolRoleWhatsAppMessage } from "../../lib/schoolFeeMessages";
import { CreditAdjustModal, PersonDetailSheet } from "./CreditAdjustModal";
import DangerConfirmModal from "../DangerConfirmModal";
import WhatsAppFollowUpPrompt from "../WhatsAppFollowUpPrompt";

/**
 * Shared person sheet + credit adjust + role confirms.
 * Used from People list and Fees (name tap).
 */
export default function PersonSheetHost({
  schoolId,
  schoolName = "",
  person,
  onClose,
  onRefresh,
}) {
  const [detailPerson, setDetailPerson] = useState(person);
  const [detailRefreshKey, setDetailRefreshKey] = useState(0);
  const [creditPerson, setCreditPerson] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [promoteAdmin, setPromoteAdmin] = useState(null);
  const [busy, setBusy] = useState(false);
  const [followUp, setFollowUp] = useState(null);

  useEffect(() => {
    setDetailPerson(person);
    setCreditPerson(null);
    setRemoveTarget(null);
    setPromoteAdmin(null);
    setDetailRefreshKey(0);
  }, [person?.id, person?.organizer_id]);

  if (!person && !detailPerson) return null;
  const active = detailPerson || person;

  function openRoleFollowUp(role) {
    setFollowUp({
      title: role === "REMOVED" ? "Team access removed" : "Role updated",
      description: `Optionally tell ${active?.name || "them"} on WhatsApp.`,
      phone: active?.phone,
      message: buildSchoolRoleWhatsAppMessage({
        studentName: active?.name,
        schoolName,
        role,
      }),
      role,
      testId: "school-role-whatsapp",
    });
  }

  async function promote(memberId, role, classIds, target) {
    setBusy(true);
    try {
      if (target?.isPerson || !memberId) {
        await api.post(`/schools/${schoolId}/members`, {
          organizerId: target.id || target.organizer_id,
          role,
          classIds: classIds || [],
        });
      } else {
        await api.patch(`/schools/${schoolId}/members/${memberId}`, { role });
      }
      toast.success("Role updated");
      setPromoteAdmin(null);
      await onRefresh?.();
      setDetailRefreshKey((k) => k + 1);
      openRoleFollowUp(role);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove() {
    if (!removeTarget) return;
    setBusy(true);
    try {
      await api.delete(`/schools/${schoolId}/members/${removeTarget.id}`);
      toast.success("Removed from team");
      setRemoveTarget(null);
      openRoleFollowUp("REMOVED");
      await onRefresh?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  function handleRoleChange(value) {
    const membership = active.membership;
    if (value === "REMOVE") {
      if (membership) setRemoveTarget(membership);
      return;
    }
    if (!membership) {
      if (value === "ADMIN") {
        setPromoteAdmin(active);
        return;
      }
      if (value === "INSTRUCTOR") {
        promote(null, "INSTRUCTOR", [], { ...active, isPerson: true });
      }
      return;
    }
    if (value === membership.role) return;
    if (value === "ADMIN") {
      setPromoteAdmin(active);
      return;
    }
    promote(membership.id, value);
  }

  return (
    <>
      {active && (
        <PersonDetailSheet
          key={active.id || active.organizer_id}
          schoolId={schoolId}
          schoolName={schoolName}
          person={active}
          refreshKey={detailRefreshKey}
          dismissOnEscape={!creditPerson && !followUp}
          busy={busy}
          onClose={() => {
            if (creditPerson || followUp) return;
            onClose?.();
          }}
          onAdjustCredits={(p) => setCreditPerson(p)}
          onRoleChange={handleRoleChange}
          onNoteSaved={() => onRefresh?.()}
        />
      )}

      {creditPerson && (
        <CreditAdjustModal
          schoolId={schoolId}
          schoolName={schoolName}
          person={creditPerson}
          onClose={() => setCreditPerson(null)}
          onDone={async (result) => {
            try {
              if (result?.balance != null) {
                setDetailPerson((cur) => (cur ? { ...cur, credit_balance: result.balance } : cur));
              }
              await onRefresh?.();
            } finally {
              setDetailRefreshKey((k) => k + 1);
            }
          }}
        />
      )}

      <DangerConfirmModal
        open={Boolean(promoteAdmin)}
        title="Make co-admin?"
        message={
          promoteAdmin
            ? `${promoteAdmin.name || "This person"} will be able to manage classes, fees, and people for this school.`
            : ""
        }
        confirmLabel="Make co-admin"
        onClose={() => setPromoteAdmin(null)}
        onConfirm={() => promote(
          promoteAdmin?.membership?.id,
          "ADMIN",
          [],
          { ...promoteAdmin, isPerson: !promoteAdmin?.membership },
        )}
        busy={busy}
        testId="school-promote-admin-confirm"
      />

      <DangerConfirmModal
        open={Boolean(removeTarget)}
        title="Remove from team?"
        message={
          removeTarget
            ? `${active?.name || removeTarget.name || "This person"} will lose school admin/instructor access. They can still follow as a student.`
            : ""
        }
        confirmLabel="Remove from team"
        onClose={() => setRemoveTarget(null)}
        onConfirm={handleRemove}
        busy={busy}
        testId="school-remove-member-confirm"
      />

      {followUp ? (
        <WhatsAppFollowUpPrompt
          title={followUp.title}
          description={followUp.description}
          phone={followUp.phone}
          message={followUp.message}
          onClose={() => {
            const shouldCloseSheet = followUp.role === "REMOVED";
            setFollowUp(null);
            if (shouldCloseSheet) onClose?.();
          }}
          testId={followUp.testId}
        />
      ) : null}
    </>
  );
}
